"use client";

import Link from "next/link";
import { BsGlobe2 } from "react-icons/bs";

import { ArticleTagType } from "@/types/article";
import { SocialLinkType } from "@/types/common";
import { SocialIconsKey, socialIconsMap } from "@/data/social";
import { ensureHttps } from "@/utils/ensureHttps";

type TrendingTopicsProps = {
  trendingTopics: ArticleTagType[];
  socialLinks: SocialLinkType[];
  lang: string;
};

export default function HeaderTrendingTopics({
  trendingTopics,
  socialLinks,
  lang,
}: TrendingTopicsProps) {
  return (
    <div className="overflow-hidden border-b h-14 bg-secondaryGray dark:bg-darkBgSecondaryColor lg:bg-transparent lg:dark:bg-transparent">
      <div className="container flex justify-between h-full gap-2">
        <div className="flex items-center gap-3 overflow-x-scroll no-scrollbar">
          <p className="hidden text-sm font-bold text-black uppercase lg:block whitespace-nowrap dark:text-white ">
            Trending Topics
          </p>
          <div className="flex items-center lg:divide-x divide-borderColor dark:divide-darkBorderColor gap-3 dark:text-white">
            {trendingTopics?.map((topic, index) => (
              <Link
                key={index}
                href={`/topics/tag/${topic?.slug}`}
                aria-label={`View all news in the tag: ${topic?.name}`}
              >
                <span className="block bg-white lg:uppercase dark:bg-darkBgPrimaryColor lg:bg-transparent lg:dark:bg-transparent dark:text-white pl-4 pr-4 lg:pr-0 lg:pl-3 
                whitespace-nowrap rounded-full text-[11px] font-medium py-1 tracking-wide transition-all lg:hover:text-primaryRed">
                  {topic?.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
        <div className="gap-6 hidden lg:flex">
          <div className="flex items-center gap-3 text-xs font-bold dark:text-white">
            {lang === "en" ? (
              <Link
                href={(process.env.NEXT_PUBLIC_CLIENT_URL as string) || ""}
                aria-label="Open the Malayalam News in a new tab"
              >
                <span className="transition-all hover:text-primaryRed">മലയാളം</span>
              </Link>
            ) : (
              <Link
                href={(process.env.NEXT_PUBLIC_EN_CLIENT_URL as string) || ""}
                aria-label="Open the English News in a new tab"
              >
                <span className="transition-all hover:text-primaryRed">ENGLISH</span>
              </Link>
            )}
            <Link href="/editorial">
              <span className="transition-all hover:text-primaryRed">EDITORIAL</span>
            </Link>
            <Link href="/special/weekend">
              <span className="transition-all hover:text-primaryRed">WEEKEND</span>
            </Link>
          </div>

          <div className="flex gap-2">
            {socialLinks?.map((socialLink, index) => {
              const Icon = socialIconsMap[socialLink?.platform as SocialIconsKey] || BsGlobe2;
              return (
                <div key={index} className="flex items-center">
                  <p className="w-6 h-6 text-sm rounded-md shrink-0 bg-[#000] text-white dark:bg-white dark:text-black flex justify-center items-center">
                    <a
                      href={ensureHttps(socialLink?.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Follow us on ${socialLink?.platform}`}
                    >
                      <Icon />
                    </a>
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
